import { createCommentVNode as _createCommentVNode, renderSlot as _renderSlot, unref as _unref, createVNode as _createVNode, openBlock as _openBlock, createElementBlock as _createElementBlock } from "vue"

import { provide, ref, watch } from 'vue';
import BookingModal from '../calendar/components/booking-modal/BookingModal.vue';
import SeriesActionChoiceDialog from '../calendar/components/SeriesActionChoiceDialog.vue';
import ConfirmationDialog from '../calendar/components/ConfirmationDialog.vue';
import { useModalState } from '../calendar/composables/useModalState.js';
import { useSeriesActionState } from '../calendar/composables/useSeriesActionState.js';
import { useConflictService } from '../calendar/services/conflictService.js';
import { useBudgetService } from '../calendar/services/budgetService.js';
import { useComplianceService } from '../calendar/services/complianceService.js';
import { useBookingManager } from '../services/bookingManager.js';
import { getBookingTimeConstraint } from '../calendar/utils/bookingTimeConstraints.js';
import { walkInEndAfterEarlyEnd } from '../calendar/utils/walkInSelection.js';
import { useMasterDataHelpers } from '../calendar/composables/useMasterDataHelpers.js';

// Initialize services and state management

export default {
  __name: 'BookingActionsProvider',
  setup(__props) {

const conflictService = useConflictService();
const budgetService = useBudgetService();
const complianceService = useComplianceService();
const { bookingManager, isSaving, isDeleting } = useBookingManager(
	conflictService,
	budgetService,
	complianceService
);

// Master data helpers for user role and user info
const { userRole, masterData, canViewOtherBookings, earliestBookingSeconds } = useMasterDataHelpers();

// Calendar refresh callbacks - stored globally so any calendar can register
const calendarRefreshCallbacks = new Set();

// Modal state management
const {
	modalState,
	closeModal: baseCloseModal,
	switchToCreateMode,
	switchToEditMode,
	switchToEditSeriesMode,
	switchToEditInstanceMode,
	openCreateModal,
	openViewModal,
	openEditModal,
} = useModalState();

// Wrap closeModal to also clear budget and compliance error state
const closeModal = () => {
	budgetService.clearBudgetError();
	complianceService.clearComplianceViolation();
	baseCloseModal();
};

// Series action state
const {
	seriesChoiceState,
	closeSeriesChoiceDialog,
	showEditSeriesChoice,
	showDeleteSeriesChoice,
} = useSeriesActionState();

// Delete confirmation state
const deleteConfirmState = ref({
	show: false,
	title: "",
	message: "",
	pendingAction: null,
});

// End walk-in booking confirmation state
const endWalkInConfirmState = ref({
	show: false,
	title: "",
	message: "",
	pendingAction: null,
});

// Time restriction info state
const timeRestrictionState = ref({
	show: false,
	title: "",
	message: "",
});

// Budget error handling is now done via budgetService

// Store drag prefill data for series operations
const dragPrefillData = ref(null);
const dragIsDragDrop = ref(false);

// Helper function to validate new booking time for drag operations
function validateNewBookingTime(newStartTime) {
	if (!newStartTime) {
		return { restricted: false };
	}
	
	const now = new Date();
	const startTime = new Date(newStartTime);
	const diffInSeconds = Math.floor((startTime.getTime() - now.getTime()) / 1000);
	
	// Check if new time is in the past
	if (startTime <= now) {
		return {
			restricted: true,
			title: 'Buchung kann nicht bearbeitet werden',
			message: 'Ihr Termin kann nicht vor die aktuelle Uhrzeit verschoben werden.'
		};
	}
	
	// Check if new time is within the earliest booking threshold (default 900 seconds = 15 minutes)
	const minSeconds = earliestBookingSeconds.value || 900;
	if (diffInSeconds < minSeconds) {
		const minMinutes = Math.ceil(minSeconds / 60);
		return {
			restricted: true,
			title: 'Buchung kann nicht bearbeitet werden',
			message: `Ihr Termin muss mind. ${minMinutes} Minuten vorher gebucht werden.`
		};
	}
	
	return { restricted: false };
}

// Public API methods that will be provided to child components
const bookingActions = {
	// Register calendar refresh callback
	registerCalendarRefresh: (refreshCallback) => {
		calendarRefreshCallbacks.add(refreshCallback);
		// Set up the unified refresh callback in booking manager
		bookingManager.setCalendarRefreshCallback(() => {
			calendarRefreshCallbacks.forEach(callback => {
				try {
					callback();
				} catch (error) {
					console.error('Calendar refresh callback failed:', error);
				}
			});
		});
	},

	// Unregister calendar refresh callback
	unregisterCalendarRefresh: (refreshCallback) => {
		calendarRefreshCallbacks.delete(refreshCallback);
	},

	// Create booking
	createBooking: (selectedSlot, prefillData = null, isWalkIn = false) => {
		if (prefillData) {
			switchToCreateMode(prefillData);
		} else {
			openCreateModal(selectedSlot, isWalkIn);
		}
	},

	// Edit booking - handles series vs instance automatically
	editBooking: (booking, prefillData = null, isDragDrop = false) => {
		// Only check time constraints for students who actually have edit permission
		if (userRole.value === 'student' && booking.permissions?.canUpdate) {
			// If this is a drag operation with new time data, validate the new time instead of original
			if (prefillData && (prefillData.startTime || prefillData.endTime)) {
				const newTimeValidation = validateNewBookingTime(prefillData.startTime);
				if (newTimeValidation.restricted) {
					showTimeRestrictionDialog(
						newTimeValidation.title,
						newTimeValidation.message
					);
					return;
				}
			} else {
				// Only check original booking constraints if it's NOT a drag operation
				const timeConstraint = getBookingTimeConstraint(booking, userRole.value);
				if (timeConstraint.restricted) {
					showTimeRestrictionDialog(
						'Buchung kann nicht bearbeitet werden',
						timeConstraint.message
					);
					return;
				}
			}
		}
		
		const hasBookingSeries = !!(booking?.series && booking.series.id);
		
		if (hasBookingSeries) {
			// Store prefill data for drag operations
			dragPrefillData.value = prefillData;
			dragIsDragDrop.value = isDragDrop;
			showEditSeriesChoice(booking);
		} else {
			openEditModal(booking, prefillData, isDragDrop);
		}
	},

	// View booking
	viewBooking: (booking) => {
		// Get current user info from master data
		const currentUserInfo = masterData.value?.userInfo;

    // Calculate if user can see all details
    const canViewDetails = canViewOtherBookings.value ||
        (booking.student && booking.student?.id === currentUserInfo.student?.id) ||
        (booking.employee && booking.employee?.id === currentUserInfo.employee?.id);
		
		// If no user info available, show modal as fallback
		if (canViewDetails) {
			openViewModal(booking);
		}
	},

	// Delete booking - handles series vs instance automatically  
	deleteBooking: (booking) => {
		// Only check time constraints for students who actually have delete permission
		if (userRole.value === 'student' && booking.permissions?.canDelete) {
			const timeConstraint = getBookingTimeConstraint(booking, userRole.value);
			if (timeConstraint.restricted) {
				showTimeRestrictionDialog(
					'Buchung kann nicht gelöscht werden',
					timeConstraint.message
				);
				return;
			}
		}
		
		const hasBookingSeries = !!(booking?.series && booking.series.id);
		
		if (hasBookingSeries) {
			showDeleteSeriesChoice(booking);
		} else {
			showDeleteConfirmation({
				type: "delete",
				data: { bookingId: booking.id },
				title: "Buchung löschen",
				message: "Möchten Sie diese Buchung wirklich unwiderruflich löschen?"
			});
		}
	},

	// Force edit series (skip choice dialog)
	editSeries: (booking) => {
		switchToEditSeriesMode(booking);
	},

	// Force edit instance (skip choice dialog)  
	editInstance: (booking) => {
		switchToEditInstanceMode(booking);
	},

	// Force delete series (skip choice dialog)
	deleteSeries: (booking) => {
		showDeleteConfirmation({
			type: "delete-series", 
			data: { seriesId: booking.series.id },
			title: "Serie löschen",
			message: "Möchten Sie die gesamte Buchungsserie wirklich unwiderruflich löschen?"
		});
	},

	// Force delete instance (skip choice dialog)
	deleteInstance: (booking) => {
		showDeleteConfirmation({
			type: "delete-instance",
			data: { bookingId: booking.id },
			title: "Einzeltermin löschen", 
			message: "Möchten Sie diesen Einzeltermin wirklich unwiderruflich löschen?"
		});
	},
};

// Provide the booking actions to child components
provide('bookingActions', bookingActions);

// Watch for budget errors and switch modal mode
watch(
	() => budgetService.budgetState.hasBudgetError,
	(hasBudgetError) => {
		if (hasBudgetError) {
			modalState.mode = 'budget-error';
			modalState.show = true;
		}
	}
);

// Watch for compliance violations and switch modal mode
watch(
	() => complianceService.complianceViolationState.hasComplianceViolation,
	(hasComplianceViolation) => {
		if (hasComplianceViolation) {
			modalState.mode = 'compliance-error';
			modalState.show = true;
		}
	}
);

// Helper function to show delete confirmation
function showDeleteConfirmation({ type, data, title, message }) {
	deleteConfirmState.value = {
		show: true,
		title,
		message,
		pendingAction: { type, data },
	};
}

// Helper function to show end-walk-in confirmation
function showEndWalkInConfirmation({ type, data, title, message }) {
	endWalkInConfirmState.value = {
		show: true,
		title,
		message,
		pendingAction: { type, data },
	};
}

// Helper function to show time restriction dialog
function showTimeRestrictionDialog(title, message) {
	timeRestrictionState.value = {
		show: true,
		title,
		message,
	};
}

// Close time restriction dialog
function closeTimeRestrictionDialog() {
	timeRestrictionState.value = {
		show: false,
		title: "",
		message: "",
	};
}

// Delete confirmation functions
function cancelDelete() {
	deleteConfirmState.value = {
		show: false,
		title: "",
		message: "",
		pendingAction: null,
	};
}

async function confirmDelete() {
	if (deleteConfirmState.value.pendingAction) {
		const action = deleteConfirmState.value.pendingAction;
		cancelDelete(); // Close dialog first
		await executeBookingAction(action);
	}
}

// End-walk-in confirmation functions
function cancelEndWalkIn() {
	endWalkInConfirmState.value = {
		show: false,
		title: "",
		message: "",
		pendingAction: null,
	};
}

async function confirmEndWalkIn() {
	if (endWalkInConfirmState.value.pendingAction) {
		const action = endWalkInConfirmState.value.pendingAction;
		cancelEndWalkIn(); // Close dialog first
		await executeBookingAction(action);
	}
}

// Unified booking action executor
async function executeBookingAction(action) {
	try {
		await bookingManager.executeBookingAction(action);
		closeModal();
	} catch (error) {
		// Error handling is done in bookingManager and services
		console.error('Booking action failed:', error);
	}
}

// Modal action handlers
async function handleBookingSave(bookingData) {
	await executeBookingAction({ type: "create", data: bookingData });
}

async function handleWalkInBookingSave(bookingData) {
	await executeBookingAction({ type: "create-walkin", data: bookingData });
}

async function handleBookingUpdate(bookingId, bookingData) {
	await executeBookingAction({
		type: "update",
		data: { bookingId, bookingData },
	});
}

async function handleSeriesUpdate(seriesId, bookingData) {
	await executeBookingAction({
		type: "update-series",
		data: { seriesId, bookingData },
	});
}

async function handleInstanceUpdate(bookingId, bookingData) {
	// modalState.booking is the pre-edit booking (still holds .series) — it's only cleared
	// by closeModal(), which runs after this action resolves. Needed so cache invalidation
	// knows this occurrence belonged to a series and can refresh sibling occurrences' logs too.
	const seriesId = modalState.booking?.series?.id ?? null;
	await executeBookingAction({
		type: "update-instance",
		data: { bookingId, bookingData, seriesId },
	});
}

async function handleBookingDelete(bookingId) {
	showDeleteConfirmation({
		type: "delete",
		data: { bookingId },
		title: "Buchung löschen",
		message: "Möchten Sie diese Buchung wirklich unwiderruflich löschen?"
	});
}

function handleEndWalkInClick(bookingId) {
	// The modal only emits the booking id - the booking itself is still in the modal state,
	// and the same helper the Beenden button uses gives the end time the backend will set.
	const newEndTime = walkInEndAfterEarlyEnd(modalState.booking);
	const endLabel = newEndTime?.toLocaleTimeString("de-DE", {
		hour: "2-digit",
		minute: "2-digit",
	});

	showEndWalkInConfirmation({
		type: "end-walkin",
		data: { bookingId },
		title: "Sofortbuchung beenden",
		message: endLabel
			? "Möchten Sie diese Sofortbuchung jetzt beenden?\n" +
				`Ihre Buchung endet damit um ${endLabel} Uhr.`
			: "Möchten Sie diese Sofortbuchung jetzt beenden?"
	});
}

function handleEditSeries(booking) {
	switchToEditSeriesMode(booking);
}

function handleEditInstance(booking) {
	switchToEditInstanceMode(booking);
}

async function handleDeleteSeries(seriesId) {
	showDeleteConfirmation({
		type: "delete-series",
		data: { seriesId },
		title: "Serie löschen",
		message: "Möchten Sie die gesamte Buchungsserie wirklich unwiderruflich löschen?"
	});
}

async function handleDeleteInstance(bookingId) {
	showDeleteConfirmation({
		type: "delete-instance",
		data: { bookingId },
		title: "Einzeltermin löschen",
		message: "Möchten Sie diesen Einzeltermin wirklich unwiderruflich löschen?"
	});
}

// Conflict resolution handlers
async function handleConflictResolution(resolutionData) {
	try {
		const originalData = conflictService.getOriginalData();
		const originalMode = conflictService.getOriginalMode();
		
		await bookingManager.handleConflictResolution(resolutionData, originalData, originalMode);
		closeModal();
	} catch (error) {
		// Error handling is done in bookingManager
		console.error("Conflict resolution failed:", error);
	}
}

function handleConflictCancel() {
	const prefillData = conflictService.getOriginalData();
	const originalMode = conflictService.getOriginalMode();
	conflictService.clearConflict();

	// Switch back to the original mode with prefill data
	if (originalMode === "edit-series") {
		switchToEditSeriesMode(modalState.booking);
		modalState.prefillData = prefillData;
	} else if (originalMode === "edit-instance") {
		switchToEditInstanceMode(modalState.booking);
		modalState.prefillData = prefillData;
	} else if (originalMode === "edit") {
		switchToEditMode(modalState.booking);
		modalState.prefillData = prefillData;
	} else {
		// Keep the Sofortbuchung form if that is where the user started
		switchToCreateMode(prefillData, { keepWalkIn: modalState.isWalkIn });
	}
}

// Handle budget error resolution (go back to edit)
function handleBudgetErrorResolve() {
	// Get original data and mode from budget service
	const originalMode = budgetService.getOriginalMode() || 'create';
	const prefillData = budgetService.getOriginalData();

	// Clear budget error
	budgetService.clearBudgetError();

	// Switch back to the original form mode with prefill data
	if (originalMode === "edit-series") {
		switchToEditSeriesMode(modalState.booking);
		modalState.prefillData = prefillData;
	} else if (originalMode === "edit-instance") {
		switchToEditInstanceMode(modalState.booking);
		modalState.prefillData = prefillData;
	} else if (originalMode === "edit") {
		switchToEditMode(modalState.booking);
		modalState.prefillData = prefillData;
	} else {
		// Create mode - keep the Sofortbuchung form if that is where the user started
		switchToCreateMode(prefillData, { keepWalkIn: modalState.isWalkIn });
	}
}

// Handle compliance error resolution (go back to edit)
function handleComplianceErrorResolve() {
	// Get original data and mode from compliance service
	const originalMode = complianceService.getOriginalMode() || 'create';
	const prefillData = complianceService.getOriginalData();

	// Clear compliance error
	complianceService.clearComplianceViolation();

	// Switch back to the original form mode with prefill data
	if (originalMode === "edit-series") {
		switchToEditSeriesMode(modalState.booking);
		modalState.prefillData = prefillData;
	} else if (originalMode === "edit-instance") {
		switchToEditInstanceMode(modalState.booking);
		modalState.prefillData = prefillData;
	} else if (originalMode === "edit") {
		switchToEditMode(modalState.booking);
		modalState.prefillData = prefillData;
	} else {
		// Create mode - keep the Sofortbuchung form if that is where the user started
		switchToCreateMode(prefillData, { keepWalkIn: modalState.isWalkIn });
	}
}

// Handle series action choice
function handleSeriesActionChoice(choice) {
	const { scope, action, booking } = choice;

	// Close the series choice dialog
	closeSeriesChoiceDialog();

	if (!booking) return;

	if (action === 'edit') {
		// Open the appropriate edit modal with drag prefill data if available
		if (scope === "series") {
			switchToEditSeriesMode(booking, dragPrefillData.value, dragIsDragDrop.value);
		} else {
			switchToEditInstanceMode(booking, dragPrefillData.value, dragIsDragDrop.value);
		}

		// Clear the stored prefill data
		dragPrefillData.value = null;
		dragIsDragDrop.value = false;
	} else if (action === 'delete') {
		// Handle delete choice
		if (scope === "series") {
			showDeleteConfirmation({
				type: "delete-series",
				data: { seriesId: booking.series.id },
				title: "Serie löschen",
				message: "Möchten Sie die gesamte Buchungsserie wirklich unwiderruflich löschen?"
			});
		} else {
			showDeleteConfirmation({
				type: "delete-instance",
				data: { bookingId: booking.id },
				title: "Einzeltermin löschen",
				message: "Möchten Sie diesen Einzeltermin wirklich unwiderruflich löschen?"
			});
		}
	}
}

// Watch for conflicts to switch modal to conflict mode
watch(
	() => conflictService.conflictState.hasConflict,
	(hasConflict) => {
		if (hasConflict) {
			modalState.mode = "conflict";
			modalState.show = true;
		}
	}
);

return (_ctx, _cache) => {
  return (_openBlock(), _createElementBlock("div", null, [
    _createCommentVNode(" Render child components "),
    _renderSlot(_ctx.$slots, "default"),
    _createCommentVNode(" Global Modals managed by provider "),
    _createVNode(BookingModal, {
      show: _unref(modalState).show,
      mode: _unref(modalState).mode,
      booking: _unref(modalState).booking,
      "selected-slot": _unref(modalState).selectedSlot,
      "prefill-data": _unref(modalState).prefillData,
      "is-loading": _unref(isSaving) || _unref(isDeleting),
      "conflict-data": _unref(conflictService).conflictState,
      "budget-error": _unref(budgetService).budgetState.errorDetails,
      "compliance-error": _unref(complianceService).complianceViolationState,
      "is-drag-drop": _unref(modalState).isDragDrop,
      "is-walk-in": _unref(modalState).isWalkIn,
      onClose: closeModal,
      onSave: handleBookingSave,
      onSaveWalkin: handleWalkInBookingSave,
      onUpdate: handleBookingUpdate,
      onUpdateSeries: handleSeriesUpdate,
      onUpdateInstance: handleInstanceUpdate,
      onDelete: handleBookingDelete,
      onEndWalkin: handleEndWalkInClick,
      onSwitchToEdit: _unref(switchToEditMode),
      onEditSeries: handleEditSeries,
      onEditInstance: handleEditInstance,
      onDeleteSeries: handleDeleteSeries,
      onDeleteInstance: handleDeleteInstance,
      onResolveConflict: handleConflictResolution,
      onCancelConflict: handleConflictCancel,
      onResolveBudgetError: handleBudgetErrorResolve,
      onResolveComplianceError: handleComplianceErrorResolve
    }, null, 8 /* PROPS */, ["show", "mode", "booking", "selected-slot", "prefill-data", "is-loading", "conflict-data", "budget-error", "compliance-error", "is-drag-drop", "is-walk-in", "onSwitchToEdit"]),
    _createCommentVNode(" Series Action Choice Dialog "),
    _createVNode(SeriesActionChoiceDialog, {
      show: _unref(seriesChoiceState).show,
      action: _unref(seriesChoiceState).action,
      booking: _unref(seriesChoiceState).booking,
      onClose: _unref(closeSeriesChoiceDialog),
      onConfirm: handleSeriesActionChoice
    }, null, 8 /* PROPS */, ["show", "action", "booking", "onClose"]),
    _createCommentVNode(" Deletion Confirmation Dialog "),
    _createVNode(ConfirmationDialog, {
      show: deleteConfirmState.value.show,
      title: deleteConfirmState.value.title,
      message: deleteConfirmState.value.message,
      "show-cancel": false,
      "confirm-text": "Löschen",
      "cancel-text": "Abbrechen",
      "confirm-button-class": "btn btn-danger",
      icon: "danger",
      "confirm-icon": "fas fa-trash",
      onClose: cancelDelete,
      onConfirm: confirmDelete
    }, null, 8 /* PROPS */, ["show", "title", "message"]),
    _createCommentVNode(" End Walk-In Booking Confirmation Dialog "),
    _createVNode(ConfirmationDialog, {
      show: endWalkInConfirmState.value.show,
      title: endWalkInConfirmState.value.title,
      message: endWalkInConfirmState.value.message,
      "show-cancel": false,
      "confirm-text": "Beenden",
      "confirm-button-class": "btn btn-warning",
      icon: "warning",
      "confirm-icon": "fas fa-stop",
      onClose: cancelEndWalkIn,
      onConfirm: confirmEndWalkIn
    }, null, 8 /* PROPS */, ["show", "title", "message"]),
    _createCommentVNode(" Time Restriction Info Dialog "),
    _createVNode(ConfirmationDialog, {
      show: timeRestrictionState.value.show,
      title: timeRestrictionState.value.title,
      message: timeRestrictionState.value.message,
      "show-cancel": false,
      "confirm-text": "Verstanden",
      "confirm-button-class": "btn btn-primary",
      icon: "warning",
      "confirm-icon": "fas fa-check",
      onClose: closeTimeRestrictionDialog,
      onConfirm: closeTimeRestrictionDialog
    }, null, 8 /* PROPS */, ["show", "title", "message"])
  ]))
}
}

}